import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Wrench, MapPin, Star, ArrowLeft, Mail, Phone, Calendar, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import AuthModal from '../components/AuthModal';
import { trackWorkerProfileView, trackJobBookingStart, trackPaymentInitiated } from '../utils/analytics';

const WorkerProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const [worker, setWorker] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [fees, setFees] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBooking, setShowBooking] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [bookingData, setBookingData] = useState({
    description: '',
    preferred_date: '',
    location: ''
  });

  useEffect(() => {
    fetchWorkerData();
    fetchFees();
  }, [id]);

  const fetchFees = async () => {
    try {
      const res = await axios.get(`${API}/fees`);
      setFees(res.data);
    } catch (error) {
      console.error('Failed to load fees:', error);
    }
  };

  const fetchWorkerData = async () => {
    try {
      const [workerRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/workers/${id}`),
        axios.get(`${API}/reviews/worker/${id}`)
      ]);
      setWorker(workerRes.data);
      setReviews(reviewsRes.data);
      
      // Track worker profile view
      trackWorkerProfileView(workerRes.data.id, workerRes.data.name);
    } catch (error) {
      toast.error('Failed to load worker profile');
      navigate('/workers');
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = () => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    if (user.role !== 'customer') {
      toast.error('Only customers can book workers');
      return;
    }
    
    // Track booking start
    trackJobBookingStart(id);
    setShowBooking(true);
  };

  const submitBooking = async () => {
    if (!bookingData.description || !bookingData.preferred_date || !bookingData.location) {
      toast.error('Please fill in all fields');
      return;
    }

    setPaymentLoading(true);
    try {
      // First create the job request (without payment initially)
      const jobRes = await axios.post(
        `${API}/jobs`,
        { worker_id: id, ...bookingData },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Track payment initiation
      trackPaymentInitiated('customer_booking', fees?.customer_booking_fee || 500);

      // Now initiate payment
      const paymentRes = await axios.post(
        `${API}/payments/create-session`,
        {
          payment_type: 'customer_booking',
          metadata: {
            job_id: jobRes.data.id,
            worker_id: id,
            worker_name: worker.name
          },
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe checkout
      window.location.href = paymentRes.data.url;
    } catch (error) {
      setPaymentLoading(false);
      toast.error(error.response?.data?.detail || 'Failed to process booking');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#003B5C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#003B5C] font-semibold">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              data-testid="back-to-directory-btn"
              onClick={() => navigate('/workers')}
              className="flex items-center space-x-2 text-[#003B5C] hover:text-[#002A42] transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-semibold">Back to Workers</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
          <div className="h-48 bg-gradient-to-br from-[#003B5C] to-[#009B4D] relative">
            {worker.profile_image ? (
              <img src={worker.profile_image} alt={worker.name} className="w-full h-full object-cover" />
            ) : (
              <div className="flex items-center justify-center h-full">
                <Wrench className="w-24 h-24 text-white/30" />
              </div>
            )}
          </div>

          <div className="p-8">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-[#0F172A]">{worker.name}</h1>
                  {worker.verified && (
                    <span className="text-[#009B4D] text-sm font-semibold bg-[#009B4D]/10 px-3 py-1 rounded-full">✓ Verified</span>
                  )}
                </div>
                <span className="inline-block bg-[#FFB81C] text-[#003B5C] px-4 py-1 rounded-full text-sm font-semibold mb-4">
                  {worker.category}
                </span>

                <div className="flex items-center space-x-1 mb-4">
                  <Star className="w-6 h-6 text-[#FFB81C] fill-[#FFB81C]" />
                  <span className="text-2xl font-bold text-[#0F172A]">{worker.rating.toFixed(1)}</span>
                  <span className="text-[#94A3B8]">({worker.review_count} reviews)</span>
                </div>

                <div className="space-y-2 text-[#475569]">
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 mr-2" />
                    <span>{worker.location}</span>
                  </div>
                  <div className="flex items-center">
                    <Briefcase className="w-5 h-5 mr-2" />
                    <span>{worker.experience_years} years experience</span>
                  </div>
                  {worker.email && (
                    <div className="flex items-center">
                      <Mail className="w-5 h-5 mr-2" />
                      <span>{worker.email}</span>
                    </div>
                  )}
                  {worker.phone && (
                    <div className="flex items-center">
                      <Phone className="w-5 h-5 mr-2" />
                      <span>{worker.phone}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="text-center md:text-right">
                <div className="mb-6">
                  <div className="text-4xl font-bold text-[#003B5C] font-['Space_Grotesk']">
                    ${worker.hourly_rate}
                  </div>
                  <div className="text-[#94A3B8]">per hour</div>
                </div>
                <Button
                  data-testid="book-worker-btn"
                  onClick={handleBooking}
                  className="bg-[#FFB81C] hover:bg-[#E5A000] text-[#003B5C] rounded-full px-8 py-3 font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full md:w-auto"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book This Worker
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* About */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-4">About</h2>
          <p className="text-[#475569] leading-relaxed">{worker.bio}</p>
        </div>

        {/* Skills */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Skills</h2>
          <div className="flex flex-wrap gap-3">
            {worker.skills.map((skill, idx) => (
              <span
                key={idx}
                className="bg-[#F9F7F2] text-[#003B5C] px-4 py-2 rounded-lg font-medium"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>

        {/* Reviews */}
        <div className="bg-white rounded-xl shadow-sm p-8">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Reviews</h2>
          {reviews.length === 0 ? (
            <p className="text-[#94A3B8]">No reviews yet</p>
          ) : (
            <div className="space-y-6">
              {reviews.map((review) => (
                <div key={review.id} className="border-b border-gray-100 pb-6 last:border-0">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-[#0F172A]">{review.customer_name}</span>
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${i < review.rating ? 'text-[#FFB81C] fill-[#FFB81C]' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-[#475569]">{review.comment}</p>
                  <p className="text-[#94A3B8] text-sm mt-2">
                    {new Date(review.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Booking Modal */}
      <Dialog open={showBooking} onOpenChange={setShowBooking}>
        <DialogContent data-testid="booking-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#003B5C]">Book {worker.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Job Description</label>
              <Textarea
                data-testid="job-description-input"
                value={bookingData.description}
                onChange={(e) => setBookingData({ ...bookingData, description: e.target.value })}
                placeholder="Describe the work you need done..."
                className="min-h-[100px]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Preferred Date</label>
              <Input
                data-testid="preferred-date-input"
                type="date"
                value={bookingData.preferred_date}
                onChange={(e) => setBookingData({ ...bookingData, preferred_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Location</label>
              <Input
                data-testid="job-location-input"
                type="text"
                value={bookingData.location}
                onChange={(e) => setBookingData({ ...bookingData, location: e.target.value })}
                placeholder="Job location in Jamaica"
              />
            </div>

            {/* Fee Breakdown */}
            {fees && (
              <div className="bg-[#F9F7F2] rounded-lg p-4 space-y-2">
                <h3 className="font-semibold text-[#0F172A] mb-3">Payment Summary</h3>
                <div className="flex justify-between text-sm">
                  <span className="text-[#475569]">Booking Fee:</span>
                  <span className="font-semibold text-[#0F172A]">{fees.currency} ${fees.customer_booking_fee.toFixed(2)}</span>
                </div>
                <div className="border-t border-gray-300 pt-2 flex justify-between">
                  <span className="font-bold text-[#0F172A]">Total to Pay:</span>
                  <span className="font-bold text-[#003B5C] text-lg">{fees.currency} ${fees.customer_booking_fee.toFixed(2)}</span>
                </div>
                <p className="text-xs text-[#94A3B8] mt-2">* Worker rates are negotiated directly. This is platform booking fee.</p>
              </div>
            )}

            <Button
              data-testid="submit-booking-btn"
              onClick={submitBooking}
              disabled={paymentLoading}
              className="w-full bg-[#FFB81C] hover:bg-[#E5A000] text-[#003B5C] rounded-full py-3 font-bold disabled:opacity-50"
            >
              {paymentLoading ? 'Processing...' : 'Proceed to Payment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {showAuth && (
        <AuthModal
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          mode={authMode}
          setMode={setAuthMode}
        />
      )}
    </div>
  );
};

export default WorkerProfilePage;